import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, ScrollText, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, money, USER_ID } from "../api";
import { addPick } from "../picks";
import { VegDot } from "../components";

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };

const ItemStoryQuiz = ({ item, accent }) => {
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!item.story && !item.quiz) return null;

  const answer = async (i) => {
    if (busy || res) return;
    setBusy(true);
    try {
      const r = await api.post(`/brands/items/${item.item_id}/quiz-answer`, { user_id: USER_ID, answer: i });
      setRes({ ...r.data, picked: i });
      if (r.data.month_locked) toast("Already counted this month — this question re-opens next cycle", { style: GOLD_TOAST });
      else if (r.data.correct && r.data.awarded) toast.success(`Correct! +${r.data.points} Urban Points`, { style: GOLD_TOAST });
      else if (!r.data.correct && r.data.penalty > 0) toast.error(`Not quite — −${r.data.penalty} points`);
    } catch {
      toast.error("Could not check your answer — please try again");
    }
    setBusy(false);
  };

  return (
    <div className="mt-2.5">
      <button data-testid={`brand-item-story-toggle-${item.item_id}`} onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest rounded-full border px-3 py-1.5"
        style={{ color: accent, borderColor: `${accent}55` }}>
        <Sparkles size={10} /> {open ? "HIDE STORY & QUIZ" : "STORY & QUIZ"}
        {item.quiz_done && <CheckCircle2 size={10} />}
      </button>
      {open && (
        <div data-testid={`brand-item-story-${item.item_id}`} className="mt-2 rounded-xl bg-[#0E0E0E] border border-white/10 p-3">
          {item.story && <p className="text-xs text-white/65 leading-relaxed">{item.story}</p>}
          {item.quiz && (
            <div className="mt-3 border-t border-white/10 pt-3" data-testid={`brand-quiz-${item.item_id}`}>
              <p className="text-[9px] tracking-widest font-black text-[#D4AF37] mb-1.5">
                QUIZ · +5 PTS {item.quiz_locked && !res ? "· ALREADY COUNTED THIS MONTH" : ""}
              </p>
              <p className="text-xs font-semibold">{item.quiz.question}</p>
              <div className="mt-2 space-y-1.5">
                {item.quiz.options.map((o, i) => {
                  let cls = "border-white/15 text-white/70";
                  if (res) {
                    if (i === res.correct_index) cls = "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10";
                    else if (i === res.picked) cls = "border-[#E25822] text-[#E25822]";
                    else cls = "border-white/10 text-white/30";
                  }
                  return (
                    <button key={i} data-testid={`brand-quiz-opt-${item.item_id}-${i}`} onClick={() => answer(i)}
                      disabled={busy || !!res}
                      className={`w-full text-left text-xs rounded-xl border px-3 py-2 transition-colors ${cls}`}>
                      {o}
                    </button>
                  );
                })}
              </div>
              {res && (
                <p data-testid={`brand-quiz-result-${item.item_id}`} className={`text-[10px] font-bold tracking-widest mt-2 ${res.correct ? "text-[#D4AF37]" : "text-[#E25822]"}`}>
                  {res.correct ? (res.awarded ? `CORRECT — +${res.points} POINTS` : res.month_locked ? "CORRECT — ALREADY COUNTED THIS MONTH" : "CORRECT") : `NOT QUITE${res.penalty > 0 ? ` — −${res.penalty} POINTS` : ""}`}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function BrandPage() {
  const { key } = useParams();
  const [brand, setBrand] = useState(null);
  const [missing, setMissing] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);

  useEffect(() => {
    setBrand(null);
    api.get(`/brands/${key}`, { params: { user_id: USER_ID } }).then((r) => setBrand(r.data)).catch(() => setMissing(true));
  }, [key]);

  if (missing) return <p className="px-4 pt-10 text-sm text-white/50">This brand isn't available.</p>;
  if (!brand) return <p className="px-4 pt-10 text-xs text-white/40">Loading…</p>;

  const paras = (brand.story || "").split("\n").filter(Boolean);

  return (
    <div data-testid={`brand-page-${brand.key}`} className="pb-10 mx-auto w-full max-w-4xl">
      <div className="px-4 pt-5 pb-4" style={{ background: `linear-gradient(180deg, ${brand.accent}1f, transparent)` }}>
        <Link to="/experience/brands" data-testid="brand-back" className="inline-flex items-center gap-1.5 text-[10px] tracking-widest text-white/50 font-bold">
          <ArrowLeft size={12} /> ALL BRANDS
        </Link>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2" style={{ color: brand.accent  }} data-brand-ink="1">{brand.name}</h1>
        <p className="text-sm text-white/60 mt-1">{brand.tagline}</p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {brand.coming_soon ? (
            <span data-testid="brand-coming-soon-banner" className="text-[9px] font-black tracking-widest rounded-full px-3 py-1.5"
              style={{ background: `${brand.accent}22`, color: brand.accent  }} data-brand-ink="1">COMING SOON</span>
          ) : brand.orderable ? (
            <span className="text-[9px] font-black tracking-widest rounded-full px-3 py-1.5 bg-[#D4AF37]/15 text-[#D4AF37]">PICKUP · PAY AT COUNTER</span>
          ) : (
            <span data-testid="brand-browse-banner" className="text-[9px] font-black tracking-widest rounded-full px-3 py-1.5 bg-white/10 text-white/60">BROWSE ONLY — SHOP IN STORE</span>
          )}
          {brand.story_status !== "approved" && (
            <span data-testid="brand-story-draft-chip" className="text-[9px] font-black tracking-widest rounded-full px-3 py-1.5 bg-[#E25822]/15 text-[#E25822]">DRAFT — REQUIRES REVIEW</span>
          )}
        </div>
      </div>

      {paras.length > 0 && (
        <div className="px-4">
          <button data-testid="brand-story-toggle" onClick={() => setStoryOpen(!storyOpen)}
            className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest rounded-full border px-4 py-2"
            style={{ color: brand.accent, borderColor: `${brand.accent}66`  }} data-brand-ink="1">
            <ScrollText size={12} /> {storyOpen ? "HIDE OUR STORY" : "OUR STORY"}
          </button>
          {storyOpen && (
            <div data-testid="brand-story-text" className="mt-3 rounded-2xl bg-[#121212] border border-white/10 p-4 space-y-3">
              {paras.map((p, i) => <p key={i} className="text-sm text-white/70 leading-relaxed">{p}</p>)}
            </div>
          )}
        </div>
      )}

      <div className="px-4 mt-5 space-y-3">
        {brand.key === "turban" ? (
          <Link to="/menu" data-testid="brand-turban-menu-link"
            className="block rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-center text-sm font-bold tracking-widest text-[#D4AF37]">
            OPEN THE FULL URBAN TURBAN MENU →
          </Link>
        ) : (
          (brand.items || []).map((it) => (
            <div key={it.item_id} data-testid={`brand-item-${it.item_id}`} className="rounded-2xl bg-[#121212] border border-white/10 p-3">
              <div className="flex items-start gap-2.5">
                {it.image && (
                  <img src={it.image} alt={it.name} loading="lazy" data-testid={`brand-item-img-${it.item_id}`}
                    className="w-16 h-16 rounded-xl object-cover shrink-0 border border-white/10"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <VegDot veg={it.vegetarian} />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold leading-snug">{it.name}</p>
                  <p className="text-sm text-white/55 leading-relaxed mt-0.5">{it.description}</p>
                </div>
                {!brand.coming_soon && it.price != null && (
                  <span className="text-base font-black text-[#D4AF37] shrink-0">{money(it.price)}</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                {brand.coming_soon ? (
                  <span data-testid={`brand-coming-soon-${it.item_id}`} className="text-[9px] font-black tracking-widest rounded-full px-3 py-1.5"
                    style={{ background: `${brand.accent}18`, color: brand.accent  }} data-brand-ink="1">
                    COMING SOON
                  </span>
                ) : brand.orderable ? (
                  <button data-testid={`brand-add-${it.item_id}`} onClick={() => addPick({ slug: it.item_id, name: it.name, price: it.price, image: it.image, vegetarian: it.vegetarian, brand: brand.key })}
                    className="h-10 px-5 rounded-full bg-[#D4AF37] text-black text-xs font-bold tracking-widest">
                    ADD
                  </button>
                ) : (
                  <span data-testid={`brand-aisle-${it.item_id}`} className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest text-white/45 border border-white/15 rounded-full px-3 py-1.5">
                    <MapPin size={10} /> {it.store_location ? `AISLE ${it.store_location}` : "AISLE INFO COMING SOON"}
                  </span>
                )}
              </div>
              <ItemStoryQuiz item={it} accent={brand.accent} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
